import { createThunklessAction } from 'thunkless';

import { avo } from '@/api/app/avo-client';

export enum OrganizationTypes {
  STRIPE_CHECKOUT_REQUEST = 'app/organization/STRIPE_CHECKOUT_REQUEST',
  STRIPE_CHECKOUT_SUCCESS = 'app/organization/STRIPE_CHECKOUT_SUCCESS',
  STRIPE_CHECKOUT_FAILURE = 'app/organization/STRIPE_CHECKOUT_FAILURE',
  STRIPE_CUSTOMER_PORTAL_REQUEST = 'app/organization/STRIPE_CUSTOMER_PORTAL_REQUEST',
  STRIPE_CUSTOMER_PORTAL_SUCCESS = 'app/organization/STRIPE_CUSTOMER_PORTAL_SUCCESS',
  STRIPE_CUSTOMER_PORTAL_FAILURE = 'app/organization/STRIPE_CUSTOMER_PORTAL_FAILURE',
  UPDATE_AUTO_FILL_REQUEST = 'app/organization/UPDATE_AUTO_FILL_REQUEST',
  UPDATE_AUTO_FILL_SUCCESS = 'app/organization/UPDATE_AUTO_FILL_SUCCESS',
  UPDATE_AUTO_FILL_FAILURE = 'app/organization/UPDATE_AUTO_FILL_FAILURE',
  GET_ORG_PROPS_REQUEST = 'app/organization/GET_ORG_PROPS_REQUEST',
  GET_ORG_PROPS_SUCCESS = 'app/organization/GET_ORG_PROPS_SUCCESS',
  GET_ORG_PROPS_FAILURE = 'app/organization/GET_ORG_PROPS_FAILURE',
  GET_SEAT_SUMMARY_REQUEST = 'app/organization/GET_SEAT_SUMMARY_REQUEST',
  GET_SEAT_SUMMARY_SUCCESS = 'app/organization/GET_SEAT_SUMMARY_SUCCESS',
  GET_SEAT_SUMMARY_FAILURE = 'app/organization/GET_SEAT_SUMMARY_FAILURE',
  GET_UPGRADE_SUMMARY_REQUEST = 'app/organization/GET_UPGRADE_SUMMARY_REQUEST',
  GET_UPGRADE_SUMMARY_SUCCESS = 'app/organization/GET_UPGRADE_SUMMARY_SUCCESS',
  GET_UPGRADE_SUMMARY_FAILURE = 'app/organization/GET_UPGRADE_SUMMARY_FAILURE',
  CANCEL_SUBSCRIPTION_REQUEST = 'app/organization/CANCEL_SUBSCRIPTION_REQUEST',
  CANCEL_SUBSCRIPTION_SUCCESS = 'app/organization/CANCEL_SUBSCRIPTION_SUCCESS',
  CANCEL_SUBSCRIPTION_FAILURE = 'app/organization/CANCEL_SUBSCRIPTION_FAILURE',
  SELF_SERVE_CHECKOUT_REQUEST = 'app/organization/SELF_SERVE_CHECKOUT_REQUEST',
  SELF_SERVE_CHECKOUT_SUCCESS = 'app/organization/SELF_SERVE_CHECKOUT_SUCCESS',
  SELF_SERVE_CHECKOUT_FAILURE = 'app/organization/SELF_SERVE_CHECKOUT_FAILURE',
  UPGRADE_PLAN_REQUEST = 'app/organization/UPGRADE_PLAN_REQUEST',
  UPGRADE_PLAN_SUCCESS = 'app/organization/UPGRADE_PLAN_SUCCESS',
  UPGRADE_PLAN_FAILURE = 'app/organization/UPGRADE_PLAN_FAILURE',
  SET_CHECKOUT_SUMMARY = 'app/organization/SET_CHECKOUT_SUMMARY',
  SET_CHECKOUT_SEAT_QUANTITY = 'app/organization/SET_CHECKOUT_SEAT_QUANTITY',
  SET_CHECKOUT_BALANCE = 'app/organization/SET_CHECKOUT_BALANCE',
  CLOSE_CHECKOUT_PROCESSING = 'app/organization/CLOSE_CHECKOUT_PROCESSING',
  CLEAR_ORG_STATUSES = 'app/organization/CLEAR_ORG_STATUSES',
  GET_BALANCE_USAGE_REQUEST = 'app/organization/GET_BALANCE_USAGE_REQUEST',
  GET_BALANCE_USAGE_SUCCESS = 'app/organization/GET_BALANCE_USAGE_SUCCESS',
  GET_BALANCE_USAGE_FAILURE = 'app/organization/GET_BALANCE_USAGE_FAILURE',
}

export interface StripeCheckoutProps {
  subdomain: string;
  planId: string;
  seatCount: number;
  gaClientId: string;
}

export const stripeCheckout = ({
  subdomain, planId, seatCount, gaClientId,
}: StripeCheckoutProps) => createThunklessAction({
  type: [
    OrganizationTypes.STRIPE_CHECKOUT_REQUEST,
    OrganizationTypes.STRIPE_CHECKOUT_SUCCESS,
    OrganizationTypes.STRIPE_CHECKOUT_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'POST',
    route: `/accounts/${subdomain}/stripe_checkout`,
    payload: { planId, seatCount, gaClientId },
  }),
});

interface StripeCustomerPortalProps { subdomain: string, updateCreditCard?: boolean }

export const stripeCustomerPortal = ({
  subdomain, updateCreditCard,
}: StripeCustomerPortalProps) => createThunklessAction({
  type: [
    OrganizationTypes.STRIPE_CUSTOMER_PORTAL_REQUEST,
    OrganizationTypes.STRIPE_CUSTOMER_PORTAL_SUCCESS,
    OrganizationTypes.STRIPE_CUSTOMER_PORTAL_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'POST',
    route: `/accounts/${subdomain}/create_customer_portal_session`,
    payload: { react_request: true, update_credit_card: updateCreditCard },
  }),
});

export interface UpdateAutoFillProps {
  id: string;
  isAutoFill: boolean;
  newAutoFillThreshold: number;
  newAutoFillBalance: number;
}

export const updateAutoFill = ({
  id, isAutoFill, newAutoFillThreshold, newAutoFillBalance,
}: UpdateAutoFillProps) => createThunklessAction({
  type: [
    OrganizationTypes.UPDATE_AUTO_FILL_REQUEST,
    OrganizationTypes.UPDATE_AUTO_FILL_SUCCESS,
    OrganizationTypes.UPDATE_AUTO_FILL_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'POST',
    route: `/organizations/${id}`,
    payload: {
      is_auto_fill: isAutoFill,
      auto_fill_threshold: newAutoFillThreshold,
      auto_fill_balance: newAutoFillBalance,
    },
  }),
});

export interface GetBalanceUsageProps {
  subdomain: string;
  from: string;
  to: string;
}

export const getBalanceUsage = ({
  subdomain, from, to,
}: GetBalanceUsageProps) => createThunklessAction({
  type: [
    OrganizationTypes.GET_BALANCE_USAGE_REQUEST,
    OrganizationTypes.GET_BALANCE_USAGE_SUCCESS,
    OrganizationTypes.GET_BALANCE_USAGE_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'GET',
    route: `/accounts/${subdomain}/balance_usage`,
    params: { from, to },
  }),
});

export interface CancelSubscriptionProps {
  subdomain: string;
  reason: string;
  feedback: string;
}

export const cancelSubscription = ({
  subdomain, reason, feedback,
}: CancelSubscriptionProps) => createThunklessAction({
  type: [
    OrganizationTypes.CANCEL_SUBSCRIPTION_REQUEST,
    OrganizationTypes.CANCEL_SUBSCRIPTION_SUCCESS,
    OrganizationTypes.CANCEL_SUBSCRIPTION_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'POST',
    route: `/accounts/${subdomain}/cancel_subscription`,
    payload: {
      cancellation: { reason, feedback },
      react_request: true,
    },
  }),
});

export interface GetOrgPropsProps { subdomain: string }

export const getOrgProps = ({ subdomain }: GetOrgPropsProps) => createThunklessAction({
  type: [
    OrganizationTypes.GET_ORG_PROPS_REQUEST,
    OrganizationTypes.GET_ORG_PROPS_SUCCESS,
    OrganizationTypes.GET_ORG_PROPS_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'GET',
    route: `/accounts/${subdomain}/org_props`,
  }),
});

export interface GetUpgradeSummary { subdomain: string }

export const getUpgradeSummary = ({ subdomain }: GetUpgradeSummary) => createThunklessAction({
  type: [
    OrganizationTypes.GET_UPGRADE_SUMMARY_REQUEST,
    OrganizationTypes.GET_UPGRADE_SUMMARY_SUCCESS,
    OrganizationTypes.GET_UPGRADE_SUMMARY_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'GET',
    route: `/accounts/${subdomain}/upgrade_summary`,
  }),
});

export interface GetSeatSummaryProps { subdomain: string }

export const getSeatSummary = ({ subdomain }: GetSeatSummaryProps) => createThunklessAction({
  type: [
    OrganizationTypes.GET_SEAT_SUMMARY_REQUEST,
    OrganizationTypes.GET_SEAT_SUMMARY_SUCCESS,
    OrganizationTypes.GET_SEAT_SUMMARY_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'GET',
    route: `/accounts/${subdomain}/seat_summary`,
  }),
});

export interface BuySegmentsProps {
  subdomain: string;
  balance: number;
}

export const addBalance = ({
  subdomain, balance,
}: BuySegmentsProps) => createThunklessAction({
  type: [
    OrganizationTypes.SELF_SERVE_CHECKOUT_REQUEST,
    OrganizationTypes.SELF_SERVE_CHECKOUT_SUCCESS,
    OrganizationTypes.SELF_SERVE_CHECKOUT_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'POST',
    route: `/accounts/${subdomain}/add_balance`,
    payload: { balance },
  }),
});

export interface BuySeatsProps {
  subdomain: string;
  avochato_seats: number;
}

export const buySeats = ({
  subdomain, avochato_seats,
}: BuySeatsProps) => createThunklessAction({
  type: [
    OrganizationTypes.SELF_SERVE_CHECKOUT_REQUEST,
    OrganizationTypes.SELF_SERVE_CHECKOUT_SUCCESS,
    OrganizationTypes.SELF_SERVE_CHECKOUT_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'POST',
    route: `/accounts/${subdomain}/buy_seats`,
    payload: { avochato_seats, react_request: true },
  }),
});

export interface UpgradePlanProps {
  subdomain: string;
  plan: string;
}

export const upgradePlan = ({
  plan, subdomain,
}: UpgradePlanProps) => createThunklessAction({
  type: [
    OrganizationTypes.SELF_SERVE_CHECKOUT_REQUEST,
    OrganizationTypes.SELF_SERVE_CHECKOUT_SUCCESS,
    OrganizationTypes.SELF_SERVE_CHECKOUT_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'POST',
    route: `/accounts/${subdomain}/upgrade_plan`,
    payload: { plan, react_request: true },
  }),
});

export interface PaymentProps {
  subdomain: string;
  plan: string;
  period: string;
  number: string;
}

export const payment = ({
  plan, period, subdomain, number,
}: PaymentProps) => createThunklessAction({
  type: [
    OrganizationTypes.UPGRADE_PLAN_REQUEST,
    OrganizationTypes.UPGRADE_PLAN_SUCCESS,
    OrganizationTypes.UPGRADE_PLAN_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'POST',
    route: `/accounts/${subdomain}/payment`,
    payload: { plan, period, number },
  }),
});

export const setCheckoutSummary = ({ checkout }) => ({
  type: OrganizationTypes.SET_CHECKOUT_SUMMARY as const,
  payload: {
    data: checkout,
  },
});

export const setCheckoutSeatQuantity = (seatQuantity) => ({
  type: OrganizationTypes.SET_CHECKOUT_SEAT_QUANTITY as const,
  payload: {
    data: seatQuantity,
  },
});

export const setCheckoutBalance = (balance) => ({
  type: OrganizationTypes.SET_CHECKOUT_BALANCE as const,
  payload: {
    data: balance,
  },
});

export const closeCheckoutProcessing = () => ({
  type: OrganizationTypes.CLOSE_CHECKOUT_PROCESSING as const,
});

export const clearOrgStatuses = () => ({
  type: OrganizationTypes.CLEAR_ORG_STATUSES as const,
});
